import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

//common
import { LoadingIndicator } from "../common/VFX-UI";

//App
import Users from "../Users/Users";
import { UserContextData } from "../../types/user";
import { SearchDefaultValue, SearchValue } from "../../store/types";
import { selectSearchValue, setSearchValue } from "../../store/user";
import {
    useActivateMutation,
    useDeleteMutation,
    useResendAccountConfirmationMutation,
    useResetPasswordMutation,
    useUnlockUserMutation,
    useUserLookupQuery,
    useUserPermissionLookupQuery,
    useUserPermissionsQuery,
    useUserStatusLookupQuery,
    useUserQuery,
    useUsersGridQuery,
} from "./hooks/useUser";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../utility/constants";
import { ContainerContentProps, FormikTypeaheadOption } from "../../types";
import { UserDto } from "../../app/model/api/types/dto";

export const UsersContext = React.createContext<UserContextData | null>(null);

interface UsersContainerProps extends ContainerContentProps {
    organizationId: string | undefined;
}

const UsersContainer = (props: UsersContainerProps) => {
    const organizationId = props.organizationId;
    const dispatch = useDispatch();
    const [userId, setUserId] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [statusId, setStatusId] = useState<string | null | undefined>(undefined);

    const searchValue: SearchValue =
        useSelector(selectSearchValue) ?? SearchDefaultValue;

    const [searchPayload, setSearchPayload] = useState<SearchValue | null>({
        name: searchValue.name,
        organizationId: organizationId,
    });

    const userLookupQuery = useUserLookupQuery();
    const userStatusLookupQuery = useUserStatusLookupQuery();
    const userQuery = useUserQuery(userId);
    useEffect(() => {
        if (statusId !== undefined || !userStatusLookupQuery.isSuccess) return;

        const activeStatus = userStatusLookupQuery.data?.result?.find(
            (status) =>
                status.code?.toLowerCase() === "active" ||
                status.name?.toLowerCase() === "active" ||
                status.display?.toLowerCase() === "active"
        );

        setStatusId(activeStatus?.id ?? null);
    }, [statusId, userStatusLookupQuery.data, userStatusLookupQuery.isSuccess]);

    const userPermissionsQuery = useUserPermissionsQuery(userId);
    const userPermissionLookupQuery = useUserPermissionLookupQuery(userId);
    const usersGridQuery = useUsersGridQuery(searchPayload, statusId);
    const { mutate: deleteMutation } = useDeleteMutation();
    const { mutate: activateMutation } = useActivateMutation();
    const { mutate: resendAccountConfirmationMutation } =
        useResendAccountConfirmationMutation();
    const { mutate: unlockUserMutation } = useUnlockUserMutation();
    const { mutate: resetPasswordMutation } = useResetPasswordMutation();
    const updateSelectedUser = async (user: UserDto | null) => {
        setSelectedUser(user);
    };


    const updateUserId = async (userId: string | null) => {
        setUserId(userId);
    };

    const updateStatusId = async (value: string | null) => setStatusId(value);

    const updateSearchPayload = async (values: SearchValue | null) => {
        if (values === null) {
            if (
                organizationId !== null &&
                organizationId !== undefined &&
                organizationId !== EMPTY_GUID
            ) {
                dispatch(
                    setSearchValue({
                        name: "",
                        organizationId: organizationId,
                    })
                );
                setSearchPayload({ name: "", organizationId: organizationId });
            } else {
                setSearchPayload(values && Object.keys(values).length ? values : null);
            }
        } else {
            if (
                organizationId !== null &&
                organizationId !== undefined &&
                organizationId !== EMPTY_GUID
            ) {
                dispatch(
                    setSearchValue({
                        name: values.name,
                        organizationId: organizationId,
                    })
                );
                values = { ...values, organizationId: organizationId };
            }
            setSearchPayload(values && Object.keys(values).length ? values : null);
        }
    };

    const contextValue: UserContextData = {
        permissionsLookupData: userPermissionLookupQuery?.data?.result,
        usersPermissionsData: userPermissionsQuery?.data?.result,
        lookupsData: userLookupQuery?.data?.result,
        data: userQuery?.data?.result ?? selectedUser ?? undefined,
        setSelectedUser: updateSelectedUser,
        id: userId,
        statusId,
        setStatusId: updateStatusId,
        statusOptions:
            userStatusLookupQuery.data?.result?.map((status) => ({
                label: status.display ?? status.name ?? "",
                value: status.id ?? "",
            })) as FormikTypeaheadOption[] ?? [],
        searchValue: searchPayload ?? undefined,
        setId: updateUserId,
        refetchGrid: () => usersGridQuery?.refetch(),
        setSearchValue: updateSearchPayload,
        pageSize: GRID_PAGE_SIZE,
    };

    return statusId === undefined || usersGridQuery.isLoading ? (
        <LoadingIndicator />
    ) : (
        <UsersContext.Provider value={contextValue}>
            <Users
                organizationId={organizationId}
                users={usersGridQuery?.data?.result}
                deleteMutation={deleteMutation}
                activateMutation={activateMutation}
                resendConfirmationEmail={resendAccountConfirmationMutation}
                unlockUserMutation={unlockUserMutation}
                resetPasswordMutation={resetPasswordMutation}
                fullscreen={props.fullscreen}
            />
        </UsersContext.Provider>
    );
};

export default UsersContainer;
